import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { avalanche } from 'viem/chains'

import { SPUSDC_POINTS_MULTIPLIER } from '../../../consts'
import { SPARK_VAULTS_V2_ENABLED } from '../../flags'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { sparkVaultsV2StatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/sparkVaultsV2'
import { sparkVaultV2StableBackedByQueryOptionsFactory } from '@/features/savings/data/savings-charts/backed-by-query/sparkVaultV2StableBackedByQuery'
import { sparkVaultV2LiquidityQueryOptionsFactory } from '@/features/savings/data/savings-charts/liquidity-query/sparkVaultV2LiquidityQuery'
import { sparkVaultsV2SavingsRateQueryOptions } from '@/features/savings/data/savings-charts/savings-rate-query/sparkVaultsV2'
import { sparkVaultV2SpusdcConverterQuery } from '@/features/savings/logic/savings-converters/sparkVaultV2Converter'

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E'),
  logo: tokenLogos.usdc,
})

const spusdc = new TokenDefinition({
  symbol: TokenSymbol('spUSDC'),
  name: 'Savings USDC',
  decimals: 6,
  address: CheckedAddress('0x28B3a8fb53B741A8Fd78c0fb9A6B2393d896a43d'),
  logo: tokenLogos.spusdc,
})

const wavax = new TokenDefinition({
  symbol: TokenSymbol('WAVAX'),
  name: 'Wrapped AVAX',
  decimals: 18,
  address: CheckedAddress('0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7'),
  logo: tokenLogos.wavax,
})

export const avalancheConfig: ChainConfigEntry = {
  originChainId: avalanche.id,
  meta: {
    name: 'Avalanche',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([usdc, spusdc, wavax]),
  nativeAssetInfo: {
    nativeAssetName: 'Avax',
    wrappedNativeAssetSymbol: TokenSymbol('WAVAX'),
    wrappedNativeAssetAddress: CheckedAddress('0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7'),
    nativeAssetSymbol: TokenSymbol('AVAX'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.03),
    nativeAssetLogo: tokenLogos.avax,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {},
  sparkLend: undefined,
  savings: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [wavax.symbol]: {
        type: 'chronicle',
        oracleAddress: CheckedAddress('0x8b3328b27436263e0BfE7597a0D97B1BEE9cC576'),
      },
      ...(SPARK_VAULTS_V2_ENABLED
        ? { [spusdc.symbol]: { type: 'vault' as const, sharesDecimals: 6, assetsDecimals: 6 } }
        : {}),
    },
    accounts: sortSavingsAccounts([
      ...(SPARK_VAULTS_V2_ENABLED
        ? [
            {
              savingsToken: spusdc.symbol,
              underlyingToken: usdc.symbol,
              supportedAssets: [usdc.symbol],
              fetchConverterQuery: sparkVaultV2SpusdcConverterQuery,
              savingsRateQueryOptions: sparkVaultsV2SavingsRateQueryOptions,
              stats: {
                queryOptions: sparkVaultsV2StatsQueryOptionsFactory('avalanche-spusdc'),
                items: ['tvl', 'users', 'deposit-cap', 'liquidity-spark-vaults-v2'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: sparkVaultV2StableBackedByQueryOptionsFactory('spusdc'),
              liquidityChartQueryOptions: sparkVaultV2LiquidityQueryOptionsFactory('spusdc'),
              sparkPointsMultiplier: SPUSDC_POINTS_MULTIPLIER,
              historyCutoff: new Date('2025-10-21T00:00:00.000Z'),
            },
          ]
        : []),
    ]),
    psmStables: undefined,
  },
  legacyFarms: undefined,
  spk: undefined,
  points: undefined,
  sparkRewards: {},
  swap: undefined,
}
